/**
 * ExportDropdown — reusable export button with CSV / Excel / PDF options.
 *
 * Usage (with pre-loaded data):
 *   <ExportDropdown headers={["Nome","Valor"]} rows={[["A","1"]]} filename="relatorio" title="Relatório" />
 *
 * Usage (fetching from backend on click):
 *   <ExportDropdown fetchCSV={() => fetch(...)} filename="relatorio" title="Relatório" />
 *
 * When fetchCSV is provided, CSV is fetched then re-exported as Excel/PDF client-side.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Download, FileText, FileSpreadsheet, FileDown, Loader2 } from "lucide-react";
import { exportCSV, exportExcel, exportPDF, parseCSV } from "@/utils/exportUtils";
import { toast } from "sonner";

export function ExportDropdown({
  // Option A: pass data directly
  headers,
  rows,
  // Option B: fetch CSV from backend (returns Response or blob)
  fetchCSV,
  // Common
  filename = "export",
  title = "",
  label = "Exportar",
  variant = "outline",
  className = "",
  // Extra button alongside (e.g. Facebook)
  extraButton,
}) {
  const [loading, setLoading] = useState(null); // "csv" | "excel" | "pdf" | null

  const getHeadersRows = async () => {
    if (headers && rows) return { headers, rows };
    if (fetchCSV) {
      const response = await fetchCSV();
      const text = response instanceof Blob
        ? await response.text()
        : typeof response === "string"
          ? response
          : await response.text();
      return parseCSV(text);
    }
    throw new Error("No data source");
  };

  const handle = async (format) => {
    setLoading(format);
    try {
      const { headers: h, rows: r } = await getHeadersRows();
      const today = new Date().toISOString().slice(0, 10);
      const base = `${filename}_${today}`;
      if (format === "csv") exportCSV(h, r, base);
      else if (format === "excel") exportExcel(h, r, base, title || filename);
      else if (format === "pdf") await exportPDF(h, r, base, title);
      toast.success(`Download ${format.toUpperCase()} iniciado`);
    } catch {
      toast.error("Erro ao exportar");
    }
    setLoading(null);
  };

  const isLoading = loading !== null;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {extraButton}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} className="gap-2" disabled={isLoading} data-testid="export-dropdown-btn">
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {isLoading ? "A exportar..." : label}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel className="text-xs text-muted-foreground">Escolher formato</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handle("csv")} className="gap-3 cursor-pointer" data-testid="export-csv">
            <FileDown className="w-4 h-4 text-green-600" />
            <div>
              <p className="font-medium text-sm">CSV</p>
              <p className="text-xs text-muted-foreground">Compativel com Excel</p>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handle("excel")} className="gap-3 cursor-pointer" data-testid="export-excel">
            <FileSpreadsheet className="w-4 h-4 text-blue-600" />
            <div>
              <p className="font-medium text-sm">Excel (.xlsx)</p>
              <p className="text-xs text-muted-foreground">Microsoft Excel</p>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handle("pdf")} className="gap-3 cursor-pointer" data-testid="export-pdf">
            <FileText className="w-4 h-4 text-red-500" />
            <div>
              <p className="font-medium text-sm">PDF</p>
              <p className="text-xs text-muted-foreground">Para imprimir ou enviar</p>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
